/**
 * Footer Organism Component
 * Site footer with logo, link columns, social links, and copyright
 */

import React from "react";
import { storyblokEditable } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Text } from "@/components/atoms/Text";
import { Image } from "@/components/atoms/Image";
import { NavItem } from "@/components/molecules/NavItem";
import { SocialLink } from "@/components/molecules/SocialLink";
import type { FooterBlok } from "@/types";

export interface FooterProps {
  blok: FooterBlok;
}

export const Footer: React.FC<FooterProps> = ({ blok }) => {
  const { logo, link_columns, social_links, copyright_text } = blok;

  return (
    <footer
      {...storyblokEditable(blok)}
      className="py-12 md:py-16 lg:py-20 xl:py-24 bg-black text-white"
    >
      <Container>
        <div className="flex flex-col items-center gap-8 md:gap-10 text-center">
          {/* Logo */}
          {logo && logo[0] && logo[0].logo_image?.filename && (
            <a href={logo[0].home_link?.cached_url || logo[0].home_link?.url || "/"} className="inline-block">
              <Image
                src={logo[0].logo_image.filename}
                alt={logo[0].alt_text || "AhaFeed"}
                className="h-8 md:h-10 w-auto"
              />
            </a>
          )}

          {/* Links */}
          <nav className="flex flex-wrap justify-center gap-6 md:gap-8">
            {link_columns &&
              link_columns.map(
                (column) =>
                  column.links &&
                  column.links.map((link) => (
                    <NavItem
                      key={link._uid}
                      {...link}
                      variant="footer"
                      className="text-sm md:text-base"
                    />
                  ))
              )}
          </nav>

          {/* Social Links */}
          {social_links && social_links.length > 0 && (
            <div className="flex gap-3 md:gap-4">
              {social_links.map((social) => (
                <SocialLink
                  key={social._uid}
                  {...social}
                  className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gray-900 hover:bg-gray-800 rounded-lg transition-all"
                />
              ))}
            </div>
          )}

          {/* Legal links + Copyright */}
          <div className="flex flex-col items-center gap-3">
            <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-gray-400">
              <a href="/privacy" className="hover:text-white transition-colors">
                Privacy
              </a>
              <a href="/terms" className="hover:text-white transition-colors">
                Terms
              </a>
              <a href="/dpa" className="hover:text-white transition-colors">
                DPA
              </a>
            </nav>
            <Text size="sm" className="text-gray-350 font-archivo font-bold">
              {`\u00a9 ${new Date().getFullYear()} ${copyright_text}`}
            </Text>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
